import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { useAuth } from "../providers/AuthProvider";
import { useStatus } from "../providers/MsgStatusProvider";
import BlueskyLogo from "./BlueskyLogo";

const BlueSkyConnectButton = () => {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const auth = useAuth();
  const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();

  const handleConnect = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setConnectionError('');
    setUsername('');
    setPassword('');
  };

  const handleSubmit = async () => {
    if (!username.trim() || !password.trim()) {
      setConnectionError('Please enter both username and password');
      return;
    }

    setIsConnecting(true);
    setConnectionError('');

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}connect/bluesky/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${auth.token}`
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.status) {
        setStatusMessage("BlueSky account connected successfully");
        setIsAlertOpen(true);
        setStatusType("success");
        
        // Emit custom event to trigger UI refresh
        window.dispatchEvent(new CustomEvent('oauthComplete', {
          detail: { platform: 'bluesky', operation: 'connected' }
        }));
        
        handleClose();
      } else {
        throw new Error(data.message || 'Failed to connect BlueSky account');
      }
    } catch (error) {
      console.error('BlueSky connection error:', error);
      setConnectionError(error.message || 'Network error occurred');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <>
      <Button variant="contained" startIcon={<BlueskyLogo />} color="primary" onClick={handleConnect}>
        Connect to BlueSky
      </Button>
      <Dialog open={open} onClose={isConnecting ? undefined : handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BlueskyLogo />
            <Typography variant="h6">Connect BlueSky Account</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {connectionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {connectionError}
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter your BlueSky username and password to connect your account.
            </Alert>
            
            <TextField
              autoFocus
              margin="dense"
              label="Username or Handle"
              placeholder="@username.bsky.social or username"
              type="text"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isConnecting}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isConnecting}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isConnecting) {
                  handleSubmit();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={isConnecting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={isConnecting || !username.trim() || !password.trim()}
            startIcon={isConnecting ? <CircularProgress size={20} /> : <BlueskyLogo />}
            sx={{
              backgroundColor: '#0085ff',
              '&:hover': {
                backgroundColor: '#0066cc',
              },
            }}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BlueSkyConnectButton;